// ============================================
// Available Jobs Page — Private & On-Demand
// With Map Route Preview Before Accepting
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import { driverMenuItems } from './driverNav';
import RouteMapPicker from '../../components/common/RouteMapPicker';
import socketService from '../../services/socket';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';

const AvailableJobItem = React.memo(({ job, previewJobId, togglePreview, handleAcceptClick, actionLoading, isVerified }) => {
    const preview = React.useMemo(() => ({
        from: job.pickupAddress,
        to: job.dropoffAddress,
        fromCoords: job.pickupLocation?.coordinates ? { lat: job.pickupLocation.coordinates[1], lng: job.pickupLocation.coordinates[0] } : null,
        toCoords: job.dropoffLocation?.coordinates ? { lat: job.dropoffLocation.coordinates[1], lng: job.dropoffLocation.coordinates[0] } : null
    }), [job]);

    const isPreviewing = previewJobId === job._id;

    return (
        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 p-6 sm:p-8 shadow-xl shadow-slate-200/50 dark:shadow-black/50 hover:shadow-2xl hover:shadow-teal-500/20 dark:hover:shadow-teal-500/10 transition-all duration-500 group overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50 dark:bg-teal-500/5 rounded-full -mr-16 -mt-16 opacity-40 group-hover:scale-150 transition-transform duration-500" />

            {/* Job Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8 relative z-10">
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                        <span className="px-4 py-1.5 rounded-xl bg-teal-50 text-teal-600 text-sm font-black tracking-tight border border-teal-100/50">
                            £{job.price.toFixed(2)}
                        </span>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            {new Date(job.scheduledDate).toLocaleDateString()} • {job.scheduledTime}
                        </span>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors uppercase leading-none">{job.name}</h3>
                </div>
                <button
                    onClick={() => isVerified && handleAcceptClick(job._id)}
                    disabled={actionLoading === job._id || !isVerified}
                    className={`w-full sm:w-auto px-10 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl shadow-black/10 transform active:scale-95 disabled:opacity-50 ${!isVerified ? 'bg-slate-400 text-white cursor-not-allowed' : 'bg-slate-900 text-white hover:bg-teal-600'}`}
                >
                    {actionLoading === job._id ? 'ACCEPTING...' : !isVerified ? 'DOCS REQUIRED' : 'ACCEPT JOB →'}
                </button>
            </div>

            {/* Address Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-8 border-t border-white/60 dark:border-white/5 relative z-10">
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/50 dark:bg-slate-800/30 border border-white/80 dark:border-white/5 hover:bg-white/70 dark:hover:bg-slate-700/30 hover:shadow-md transition-all group/loc">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-white/5 group-hover/loc:scale-110 transition-transform">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-1.5">Pickup Address</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200 truncate">{job.pickupAddress}</p>
                    </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-white/50 dark:bg-slate-800/30 border border-white/80 dark:border-white/5 hover:bg-white/70 dark:hover:bg-slate-700/30 hover:shadow-md transition-all group/loc">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700/50 flex items-center justify-center text-slate-500 dark:text-slate-400 shadow-sm border border-slate-100 dark:border-white/5 group-hover/loc:scale-110 transition-transform">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest leading-none mb-1.5">Dropoff Address</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200 truncate">{job.dropoffAddress}</p>
                    </div>
                </div>
            </div>

            {/* Route Preview Toggle Button */}
            <div className="mt-5 relative z-10">
                <button
                    onClick={() => togglePreview(job._id)}
                    className={`w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all transform active:scale-95 ${
                        isPreviewing
                            ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
                            : 'bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                    }`}
                >
                    <span className="text-base">
                        <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
                    </span>
                    {isPreviewing ? 'Hide Route Preview' : 'Preview Route on Map'}
                    <svg className={`w-4 h-4 transition-transform duration-300 ${isPreviewing ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Map Preview Panel */}
                {isPreviewing && (
                    <div className="mt-4 transition-all animate-fadeIn">
                        <div className="rounded-2xl overflow-hidden border border-teal-100">
                            <div className="bg-teal-50 px-4 py-2.5 flex items-center gap-2 border-b border-teal-100">
                                <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                                <span className="text-[11px] font-black text-teal-700 uppercase tracking-widest">Route Direction Preview</span>
                            </div>
                             <RouteMapPicker
                                fromAddress={preview.from}
                                toAddress={preview.to}
                                fromCoords={preview.fromCoords}
                                toCoords={preview.toCoords}
                                height="300px"
                                theme="teal"
                                readOnly={true}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});
AvailableJobItem.displayName = 'AvailableJobItem';

const AvailableJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, jobId: null });
    const [previewJobId, setPreviewJobId] = useState(null); // which job has its map open
    const [unverifiedApi, setUnverifiedApi] = useState(false);
    const { showToast } = useToast();
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const isUnverified = !user?.isVerified || unverifiedApi;

    const fetchJobs = useCallback(async () => {
        try {
            const res = await api.get('/driver/available-jobs');
            setUnverifiedApi(false);
            setJobs(res.data.data);
        } catch (error) {
            // If 403 unverified, clear jobs and mark unverified
            if (error.response?.status === 403) {
                setUnverifiedApi(true);
                setJobs([]);
            } else {
                console.error('Fetch jobs error:', error);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        fetchJobs(); 
        const handleJobAssigned = (job) => {
            showToast('info', `New job assigned: ${job.name}`);
            fetchJobs();
        };
        const handleNewJob = () => {
            fetchJobs();
        };
        const handleJobUpdated = () => {
            fetchJobs();
        };
        socketService.on('job:assigned', handleJobAssigned);
        socketService.on('job:new', handleNewJob);
        socketService.on('job:updated', handleJobUpdated);

        return () => {
            socketService.off('job:assigned', handleJobAssigned);
            socketService.off('job:new', handleNewJob);
            socketService.off('job:updated', handleJobUpdated);
        };
    }, [fetchJobs, showToast]);

    const handleAcceptClick = (jobId) => {
        setConfirmModal({ isOpen: true, jobId });
    };

    const confirmAcceptJob = async () => {
        const jobId = confirmModal.jobId;
        setConfirmModal({ isOpen: false, jobId: null });
        if (!jobId) return;

        setActionLoading(jobId);
        setMessage({ text: '', type: '' });
        try {
            await api.patch(`/driver/jobs/${jobId}/accept`, {});
            setMessage({ text: 'Job accepted! It will now appear on your dashboard.', type: 'success' });
            fetchJobs();
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
        } catch (error) {
            setMessage({ text: 'Error accepting job: ' + (error.response?.data?.message || error.message), type: 'error' });
            setTimeout(() => setMessage({ text: '', type: '' }), 5000);
        } finally {
            setActionLoading(null);
        }
    };

    const togglePreview = (jobId) => {
        setPreviewJobId((prev) => (prev === jobId ? null : jobId));
    };

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-100 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative">
            <Sidebar items={driverMenuItems} theme="teal" title="IdaCab Driver" />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Header */}
                <DriverMobileHeader user={user} theme={theme} toggleTheme={toggleTheme} />

                {/* Desktop Header */}
                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-teal-600 to-teal-400 bg-clip-text text-transparent tracking-tighter">Available Private Jobs</h2>
                </header>

                <main className="flex-1 overflow-y-auto px-4 pt-2 pb-28 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-5xl mx-auto space-y-8">
                        {/* Hero Banner */}
                        <div className="relative bg-gradient-to-br from-teal-600/90 to-teal-700/90 dark:from-teal-800/80 dark:to-teal-950/80 backdrop-blur-3xl p-8 rounded-[2.5rem] text-white shadow-2xl shadow-teal-600/20 dark:shadow-black/50 border border-teal-400/20 overflow-hidden group hover:shadow-teal-500/30 transition-all duration-500">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 dark:bg-white/5 rounded-full blur-3xl group-hover:bg-white/20 transition-all duration-700" />
                            <div className="relative z-10">
                                <h3 className="text-xl font-black flex items-center gap-3 mb-2 uppercase tracking-tight">
                                    <span className="w-10 h-10 rounded-2xl bg-white/20 dark:bg-white/10 flex items-center justify-center shadow-inner">
                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    </span>
                                    Boost Your Earnings
                                </h3>
                                <p className="text-xs sm:text-sm text-teal-50 dark:text-teal-100 leading-relaxed font-medium max-w-2xl">
                                    On-demand private jobs requested by users. First-come, first-served.
                                    <span className="block mt-2 font-black uppercase tracking-widest text-[10px] flex items-center gap-1.5">
                                        <svg className="w-3 h-3 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" /></svg>
                                        Preview the route on the map before accepting.
                                    </span>
                                </p>
                            </div>
                        </div>

                        {/* Unverified Driver Warning */}
                        {isUnverified && (
                            <div className="bg-slate-950 p-6 sm:p-8 rounded-[2.5rem] text-white shadow-2xl shadow-slate-900/40 relative overflow-hidden">
                                <div className="absolute -right-20 -top-20 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
                                <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                                    <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-500">
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-base font-black uppercase tracking-tight mb-1">Account Unverified</h3>
                                        <p className="text-xs text-slate-400 font-medium">You cannot accept jobs until your documents are verified and approved.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Toast Message */}
                        {message.text && (
                            <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl transition-all font-bold tracking-tight text-sm animate-bounce-short ${
                                message.type === 'success'
                                    ? 'bg-emerald-500 text-white shadow-emerald-500/30'
                                    : 'bg-rose-500 text-white shadow-rose-500/30'
                            }`}>
                                <span className="text-xl">{message.type === 'success' ? (
                                    <svg className="w-5 h-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                ) : (
                                    <svg className="w-5 h-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                )}</span>
                                {message.text}
                            </div>
                        )}

                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : jobs.length > 0 ? (
                            <div className="grid gap-4 sm:gap-6">
                                {jobs.map((job) => (
                                    <AvailableJobItem
                                        key={job._id}
                                        job={job}
                                        previewJobId={previewJobId}
                                        togglePreview={togglePreview}
                                        handleAcceptClick={handleAcceptClick}
                                        actionLoading={actionLoading}
                                        isVerified={!isUnverified}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-24 bg-white/50 dark:bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] border border-dashed border-slate-300/50 dark:border-slate-700/50 shadow-inner">
                                <div className="w-24 h-24 rounded-full bg-white/50 dark:bg-slate-700/30 flex items-center justify-center mx-auto mb-8 opacity-30 shadow-inner border border-white/60 dark:border-white/5">
                                    <svg className="w-12 h-12 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" /></svg>
                                </div>
                                <h3 className="font-black text-slate-900 dark:text-white text-xl uppercase tracking-tight">No Jobs Available</h3>
                                <p className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-3">Check back soon for new opportunities</p>
                            </div>
                        )}
                    </div>

                    {/* Confirm Modal — unchanged */}
                    {confirmModal.isOpen && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                            <div
                                className="absolute inset-0 bg-slate-900/60 dark:bg-black/60 backdrop-blur-sm transition-opacity"
                                onClick={() => setConfirmModal({ isOpen: false, jobId: null })}
                            />
                            <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-3xl rounded-[2.5rem] shadow-2xl dark:shadow-black/50 w-full max-w-sm overflow-hidden animate-slide-up border border-white/60 dark:border-white/10">
                                <div className="p-8 text-center">
                                    <div className="w-16 h-16 bg-amber-50 dark:bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner rotate-3 border border-amber-100 dark:border-amber-500/20">
                                        <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01" /><circle cx="12" cy="12" r="10" /></svg>
                                    </div>
                                    <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2 uppercase tracking-tight">Accept Job?</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-8">
                                        Are you sure you want to accept this private job? Once accepted, you must complete it or contact dispatch.
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setConfirmModal({ isOpen: false, jobId: null })}
                                            className="px-4 py-3.5 rounded-xl text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 font-bold text-sm tracking-wide transition-colors border border-transparent dark:border-white/5"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmAcceptJob}
                                            className="px-4 py-3.5 rounded-xl text-white bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-600/30 font-bold text-sm tracking-wide transition-all transform active:scale-95"
                                        >
                                            Yes, Accept
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>

            <BottomNav items={driverMenuItems} />
        </div>
    );
};

export default AvailableJobs;
